import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    NavLink,
    useParams,
} from 'react-router-dom';

import {
    Button,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from 'react-bootstrap';

import { Formik } from 'formik';

import API from '../../api/axios';

import useCompanyContext from '../../hooks/useCompanyContext';

import useDiscountPermissions from './hooks/useDiscountPermissions';
import useAssignShops from './hooks/useAssignShops';

import DiscountBasicFields from './components/DiscountBasicFields';
import DiscountShopTable from './components/DiscountShopTable';
import AssignShopsModal from './components/AssignShopsModal';

import {
    buildOfferPayload,
    mapOfferDetailToFormValues,
} from './utils/discountMappers';

const FormSection = ({
    title,
    level = 1,
    children,
    button = null,
}) => {
    const Tag =
        `h${Math.min(
            6,
            level + 3,
        )}`;

    return (
        <section className="mb-4 form-section">
            <Tag
                className={
                    `text-muted ${level === 1
                        ? 'fw-semibold'
                        : 'fw-medium'
                    }`
                }
            >
                {title} {button}
            </Tag>

            {children}
        </section>
    );
};

export default function DiscountFormPage() {
    const {
        isAdmin,
        isAllCompanies,
        currentCompanyID,
        buildCompanyUrl,
    } = useCompanyContext();

    const {
        isGlobalAdmin,
        hideRmbDiscount,
    } = useDiscountPermissions({
        isAdmin,
        isAllCompanies,
    });

    const { id } =
        useParams();

    const isEdit =
        Boolean(id);

    const baseInitialValues =
        useMemo(
            () => ({
                validStartDate:
                    new Date(),

                validEndDate:
                    new Date(),

                rentStartDate:
                    new Date(),

                rentEndDate:
                    new Date(),

                rmbDiscount: '',
                supplierDiscount: '',
                minDay: 0,
                shops: [],
            }),
            [],
        );

    const [
        initialValues,
        setInitialValues,
    ] = useState(
        baseInitialValues,
    );

    const [
        loading,
        setLoading,
    ] = useState(
        isEdit,
    );

    const [
        error,
        setError,
    ] = useState(null);

    const assignShops =
        useAssignShops({
            isGlobalAdmin,
            currentCompanyID,
        });

    const loadOffer =
        useCallback(async () => {
            if (!isEdit) {
                return null;
            }

            const {
                data,
            } =
                await API.get(
                    `/offer/${id}`,
                );

            return data;
        }, [
            id,
            isEdit,
        ]);

    useEffect(() => {
        if (!isEdit) {
            return undefined;
        }

        let cancelled = false;

        const load =
            async () => {
                try {
                    setLoading(
                        true,
                    );

                    setError(
                        null,
                    );

                    const data =
                        await loadOffer();

                    if (cancelled) {
                        return;
                    }

                    setInitialValues(
                        mapOfferDetailToFormValues(
                            data,
                            baseInitialValues,
                        ),
                    );
                } catch (err) {
                    console.error(
                        err,
                    );

                    if (
                        !cancelled
                    ) {
                        setError(
                            err?.response
                                ?.data
                                ?.message ||
                            err?.message ||
                            'Failed to load offer',
                        );
                    }
                } finally {
                    if (
                        !cancelled
                    ) {
                        setLoading(
                            false,
                        );
                    }
                }
            };

        load();

        return () => {
            cancelled = true;
        };
    }, [
        baseInitialValues,
        isEdit,
        loadOffer,
    ]);

    const submitOffer =
        useCallback(
            async (values) => {
                const payload =
                    buildOfferPayload(
                        values,
                        {
                            hideRmbDiscount,
                        },
                    );

                if (isEdit) {
                    await API.patch(
                        `/offer/${id}`,
                        payload,
                    );

                    alert(
                        'Offer updated successfully!',
                    );

                    return;
                }

                await API.post(
                    '/offer',
                    payload,
                );

                alert(
                    'Offer created successfully!',
                );
            },
            [
                hideRmbDiscount,
                id,
                isEdit,
            ],
        );

    if (loading) {
        return (
            <Container className="py-4">
                <div className="d-flex align-items-center gap-2 text-muted">
                    <Spinner
                        animation="border"
                        size="sm"
                    />

                    <span>
                        Loading...
                    </span>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-4">
                <div className="text-danger">
                    {error}
                </div>
            </Container>
        );
    }

    return (
        <Formik
            initialValues={
                initialValues
            }
            enableReinitialize
            onSubmit={
                submitOffer
            }
        >
            {({
                handleSubmit,
                values,
                setFieldValue,
            }) => (
                <Container
                    fluid
                    className="d-flex flex-column"
                >
                    <Row className="mb-4">
                        <Col sm={8}>
                            <span className="fw-semibold">
                                Discount
                                Information
                            </span>
                        </Col>

                        <Col
                            sm={4}
                            className="text-end"
                        >
                            <span className="fw-semibold">
                                <NavLink
                                    to={buildCompanyUrl(
                                        '/dashboard',
                                    )}
                                    className="block-link"
                                >
                                    Dashboard
                                </NavLink>{' '}
                                - Discount
                                Information
                            </span>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <Card className="h-100 p-2">
                                <Card.Body>
                                    <Row>
                                        <Col
                                            sm={
                                                12
                                            }
                                            md={
                                                6
                                            }
                                        >
                                            <FormSection
                                                title="Main Information"
                                                level={
                                                    2
                                                }
                                            >
                                                <DiscountBasicFields
                                                    values={
                                                        values
                                                    }
                                                    setFieldValue={
                                                        setFieldValue
                                                    }
                                                    hideRmbDiscount={
                                                        hideRmbDiscount
                                                    }
                                                />
                                            </FormSection>
                                        </Col>

                                        <Col
                                            sm={
                                                12
                                            }
                                            md={
                                                6
                                            }
                                        >
                                            <FormSection
                                                title="Discount Shops"
                                                level={
                                                    2
                                                }
                                                button={
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() =>
                                                            assignShops.openAssignModal(
                                                                values.shops,
                                                            )
                                                        }
                                                    >
                                                        + New
                                                    </Button>
                                                }
                                            >
                                                <DiscountShopTable
                                                    values={
                                                        values
                                                    }
                                                />
                                            </FormSection>
                                        </Col>

                                        <Col
                                            sm={
                                                12
                                            }
                                            className="gap-3 mt-2 text-end"
                                        >
                                            <Button
                                                type="button"
                                                onClick={
                                                    handleSubmit
                                                }
                                                className="border ms-1 btn-custom-success"
                                            >
                                                Save
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <AssignShopsModal
                        show={
                            assignShops.showAssignModal
                        }
                        onHide={
                            assignShops.closeAssignModal
                        }
                        isGlobalAdmin={
                            isGlobalAdmin
                        }
                        loadingOptions={
                            assignShops.loadingOptions
                        }
                        assignmentMode={
                            assignShops.assignmentMode
                        }
                        selectedSupplier={
                            assignShops.selectedSupplier
                        }
                        selectedLocation={
                            assignShops.selectedLocation
                        }
                        supplierOptions={
                            assignShops.supplierOptions
                        }
                        globalLocationOptions={
                            assignShops.globalLocationOptions
                        }
                        scopedLocationOptions={
                            assignShops.scopedLocationOptions
                        }
                        modalShops={
                            assignShops.modalShops
                        }
                        modalSelection={
                            assignShops.modalSelection
                        }
                        selectAll={
                            assignShops.selectAll
                        }
                        onAssignmentModeChange={
                            assignShops.handleAssignmentModeChange
                        }
                        onSupplierChange={
                            assignShops.handleSupplierChange
                        }
                        onLocationChange={
                            assignShops.handleLocationChange
                        }
                        onToggleSelectAll={
                            assignShops.handleToggleSelectAll
                        }
                        onToggleOne={
                            assignShops.handleToggleOne
                        }
                        onConfirm={() => {
                            const selected =
                                assignShops.modalShops.filter(
                                    (shop) =>
                                        assignShops.modalSelection[
                                        shop.id
                                        ],
                                );

                            const toAdd =
                                selected.map(
                                    (shop) => ({
                                        shop_id:
                                            shop.id,

                                        shop_name:
                                            shop.name,

                                        shop_location:
                                            shop.locationName,

                                        supplier_company_name:
                                            shop.companyName,
                                    }),
                                );

                            const current =
                                Array.isArray(
                                    values.shops,
                                )
                                    ? values.shops
                                    : [];

                            const merged = [
                                ...current,
                            ];

                            const exist =
                                new Set(
                                    merged.map(
                                        (
                                            item,
                                        ) =>
                                            item.shop_id,
                                    ),
                                );

                            toAdd.forEach(
                                (item) => {
                                    if (
                                        !exist.has(
                                            item.shop_id,
                                        )
                                    ) {
                                        merged.push(
                                            item,
                                        );

                                        exist.add(
                                            item.shop_id,
                                        );
                                    }
                                },
                            );

                            setFieldValue(
                                'shops',
                                merged,
                                false,
                            );

                            assignShops.closeAssignModal();
                        }}
                    />
                </Container>
            )}
        </Formik>
    );
}